import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function TorbayCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Torbay Pothole Claims | 58% More Potholes Filled in Four Years | Fixtyer"
        description="Torbay Council filled 4,552 potholes in 2024/25 — up 58.1% since 2021/22 — while admitting a progressively deteriorating network. B and C roads in RED condition rose from 5.3% to 8.7%. See what the DfT AMBER rating means for your claim."
        canonical="/councils/torbay"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Torbay', url: '/councils/torbay' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'The DfT rates Torbay GREEN for condition — can I still claim?',
            answer: 'Yes. The DfT Condition scorecard is GREEN at network level, but Torbay\'s own SCANNER data shows B and C roads in RED condition rose from 5.3% in 2020 to 8.7% in 2024, and A-road RED rose from 2.6% to 3.9% in the same period. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on an aggregate scorecard that can mask local deterioration.',
          },
          {
            question: 'What if my pothole was on an estate or residential road?',
            answer: 'Unclassified roads make up 457.5km — 79% of Torbay\'s 576km network — and are predominantly estate roads. SCANNER surveys cover only the highest maintenance hierarchy U-roads, not the full estate network. SWISCo has commissioned a separate two-year RoadMetrics AI assessment of U-roads using PAS2161 methodology. If your road was outside the SCANNER route, network-level condition data may not exist for it.',
          },
          {
            question: 'Torbay inspects every road twice a year — does that block Section 58?',
            answer: 'Not automatically. Torbay states each part of the highway will be inspected at least twice per year, and reports defects within five working days. But the council also admits a progressively deteriorating network and expects pothole attendances to keep rising. Twice-yearly driven inspections do not guarantee every defect is caught — especially on a network where reactive repairs climbed 58.1% in four years. Prior reports and photographic evidence of defect age remain decisive.',
          },
          {
            question: 'Does Torbay\'s 2024 risk-based inspection overhaul affect my claim?',
            answer: 'It may help or hinder depending on timing. Torbay overhauled its Highway Safety Inspection Manual in 2024, moving to a fully risk-based approach aligned with Well-Managed Highway Infrastructure guidance. If your incident occurred after the change, the council may argue its new system was reasonable. If the pothole was reported before the overhaul and not repaired, or if the defect predates the new criteria, the prior inspection regime and response times are still relevant.',
          },
          {
            question: 'Why did pothole repairs rise 58.1% while condition is rated GREEN?',
            answer: 'Torbay filled 2,878 potholes in 2021/22 and 4,552 in 2024/25 — a 58.1% increase the council publishes itself. It simultaneously describes a progressively deteriorating network and projects at least 4,870 pothole repairs in 2025/26. Rising reactive workload on a network valued at over £700 million suggests defects are forming faster than structural renewal can keep pace, even where aggregate SCANNER scores remain acceptable.',
          },
          {
            question: 'Who maintains the dual carriageway between Paignton, Brixham and Torquay?',
            answer: 'Torbay\'s transparency report states maintenance responsibility for the high-speed dual carriageway between the South Devon Link Road and the eastern and western corridors linking Paignton and Brixham to Torquay is shared with Devon County Council. If your damage was on that section, establish which authority had maintenance duty at the precise location before submitting a claim.',
          },
          {
            question: 'Do I need to report the pothole before claiming?',
            answer: 'Torbay\'s highway maintenance guidance states it inspects all reports within five working days and encourages residents to report defects it cannot catch on cyclic inspections alone. Reporting creates a dated council record — useful if the pothole was reported before your incident, or if SWISCo failed to repair it within a reasonable time. Keep your reference number and any confirmation.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Torbay Council"
        councilSlug="torbay"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/torbay"
        headline="Torbay Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Torbay</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Torbay: 58% more potholes filled on a deteriorating network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Torbay Council&apos;s own transparency report records a{' '}
            <strong className="text-rose-400">58.1% rise in potholes filled</strong> between 2021/22 and
            2024/25 — from 2,878 to 4,552 — while describing a{' '}
            <strong className="text-amber-400">progressively deteriorating network</strong>. The DfT rates
            overall maintenance <strong className="text-amber-400">AMBER</strong> with{' '}
            <strong className="text-emerald-400">GREEN</strong> condition, but SCANNER data shows B and C roads
            in RED condition climbed from <strong className="text-rose-400">5.3% to 8.7%</strong> since 2020 on
            a 576km network where estate roads make up 79% of all carriageways.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">58.1%</div>
            <div className="text-white font-medium mt-2">Rise in potholes filled since 2021/22</div>
            <div className="text-brand-300 text-sm mt-1">
              2,878 potholes in 2021/22 rising to 4,552 in 2024/25 — roughly 12 repairs every day. The council
              expects at least 4,870 in 2025/26 as the asset continues to deteriorate.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Torbay&apos;s own transparency report — classified roads
            slipping even as the DfT awards a GREEN condition scorecard
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (55km — 9.5% of network): slipping in 2024</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3.9%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 2.6% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">23.8%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 20% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">72.5%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 77.4% in 2023</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Torbay&apos;s strategic A380 and A385 corridors link Devon to the national network. After four
              years of stable condition, 2024 brought the sharpest deterioration on record — green A-roads
              fell nearly five percentage points in a single survey year.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (63.5km — 11% of network): declining steadily</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">33.2%</td>
                    <td className="px-4 py-2 text-center">61.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">33.2%</td>
                    <td className="px-4 py-2 text-center">61.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.4%</td>
                    <td className="px-4 py-2 text-center">33.1%</td>
                    <td className="px-4 py-2 text-center">61.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6.4%</td>
                    <td className="px-4 py-2 text-center">32.3%</td>
                    <td className="px-4 py-2 text-center">61.2%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8.7%</td>
                    <td className="px-4 py-2 text-center font-bold">31.4%</td>
                    <td className="px-4 py-2 text-center font-bold">59.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B and C roads in RED condition are up 64% since 2020 (5.3% → 8.7%), and good-condition roads
              have fallen from 61.4% to 59.9%. More than 40% of Torbay&apos;s classified local roads now need
              — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN condition, AMBER spend</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£2.8m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£2.9m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">28%</div>
                <div className="text-brand-300 text-sm">Reactive maintenance share (2025/26 projected)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Torbay&apos;s capital spend tracks its DfT allocation closely — not the overspend pattern of
              better-funded authorities — while reactive maintenance has climbed from 24% to a projected 28%.
              On a network valued at over <span className="text-amber-400">£700 million</span>, the 2025/26
              plan treats just 9.7km of carriageway structurally (2.4km resurface, 7.3km thin overlay or surface
              dress, 0.4km reconstruction) against 576km of roads.
            </p>
          </div>
        </div>
      </section>

      {/* The estate road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 457.5km estate road majority
          </h2>
          <p className="text-surface-600 mb-8">
            79% of Torbay&apos;s network is unclassified roads — surveyed selectively, not comprehensively
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition (SCANNER route)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4.13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4.13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">3%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The selective survey route</h3>
              <p className="text-surface-600 text-sm mb-3">
                Torbay commissions annual UKPMS SCANNER surveys through SWISCo and WDM on a predetermined route
                covering the classified network and the <strong>highest maintenance hierarchy</strong> unclassified
                roads — not every estate street. The U Class network is predominantly estate roads, with some
                strategic routes to wards and trading estates.
              </p>
              <p className="text-surface-600 text-sm">
                If your pothole was on a residential estate road outside the SCANNER hierarchy, the published
                U-road RED percentages may not reflect the condition of your specific carriageway.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The RoadMetrics AI assessment</h3>
              <p className="text-amber-800 text-sm mb-3">
                SWISCo has commissioned RoadMetrics AI for a two-year assessment of the full U Class network
                using artificial intelligence survey methods, adopting PAS2161 and consulting the DfT on the
                emerging data standard. From 2026/27, PAS2161 will replace the current three-category SCANNER
                system nationally.
              </p>
              <p className="text-amber-800 text-sm">
                This parallel survey is still in progress. Until it completes, estate road condition knowledge
                rests on cyclic safety inspections and public reports — not comprehensive laser survey data.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Torbay&apos;s estate network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road on the SCANNER survey route or only covered by driven safety inspections?</li>
              <li>• If 5% of surveyed U-roads were RED in 2022/23, what was done about defects on yours?</li>
              <li>• Does twice-yearly inspection actually cover every estate road, or only carriageway categories in the hierarchy?</li>
              <li>• Has the 2024 risk-based inspection overhaul changed response times for your road class?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge from a selective SCANNER route covering a
              fraction of its 457.5km of estate roads.
            </p>
          </div>
        </div>
      </section>

      {/* The repair conveyor belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            14,974 potholes in four years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Year-on-year change</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,878</td>
                  <td className="px-6 py-4 text-center text-surface-400">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,303</td>
                  <td className="px-6 py-4 text-center">+14.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,241</td>
                  <td className="px-6 py-4 text-center text-rose-600">+28.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,552</td>
                  <td className="px-6 py-4 text-center">+7.0%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,974</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+58.1% overall</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The council expects the trend to continue</h3>
              <p className="text-surface-600 text-sm">
                Torbay states that due to a deteriorating asset, it will be consistently increasing the amount
                of potholes and defects it attends to. It projects at least 4,870 pothole repairs in 2025/26 —
                a further 7% rise. A network producing defects at this rate is one where potholes routinely
                form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Right first time — in theory</h3>
              <p className="text-amber-800 text-sm">
                Torbay&apos;s 2025/26 strategy centres on a &lsquo;right first time&rsquo; approach through
                SWISCo, with new excavation equipment intended to produce more permanent patching and fewer
                reactive temporary repairs. The council is investigating whether this reduces re-attendance
                rates. Until those results are published, the rising pothole count is the measurable reality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The deteriorating network admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The deteriorating network admission
          </h2>
          <p className="text-surface-600 mb-8">
            Torbay&apos;s own explanation for rising repairs and reconstruction — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Due to rising costs and a progressively deteriorating network, there has been an increase in reconstruction and resurfacing projects. Thin overlay sites are now mainly focused on refreshing entire estates and areas with the highest skid resistance risks."
              source="Torbay Council Local Highways Maintenance Transparency Report 2025/2026"
            />
            <QuoteCard
              quote="We estimate that due to a deteriorating asset, we will be consistently increasing the amount of 'potholes and defects we attend to, however with the introduction of new methods of excavation with the new equipment, we believe that we will be undertaking more patching and less reactive temporary repairs."
              source="Torbay Council Local Highways Maintenance Transparency Report 2025/2026"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Torbay formally acknowledges its highway asset is progressively deteriorating and that pothole
                attendances will keep rising. That is documented knowledge of an elevated defect-formation
                rate across the network — not an isolated bad winter.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerating deterioration raises the standard for what a &lsquo;reasonable&rsquo;
                inspection and repair regime looks like under Section 58, particularly on estate roads where
                structural treatment is concentrated on entire-area thin overlay schemes.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2024 inspection overhaul</h3>
              <p className="text-amber-800 text-sm mb-3">
                Torbay overhauled its Highway Safety Inspection Manual in 2024, moving fully to a risk-based
                approach aligned with Well-Managed Highway Infrastructure guidance. The stated aim is fewer
                site re-attendances and defects addressed correctly the first time.
              </p>
              <p className="text-amber-800 text-sm">
                If your incident post-dates the overhaul, the council may cite the new manual. If your pothole
                was reported under the prior regime and left unrepaired, the earlier inspection criteria and
                response times remain directly relevant to your claim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN condition means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN-condition AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Torbay is not failing on every metric — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard at network aggregate level
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management policy and 2024 risk-based inspection manual
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Twice-yearly highway inspections across the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 72–76% of spend classed as preventative over recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED on SCANNER route fell to 3% in 2024
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing inspection frequency and asset management policy.
                Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B and C roads in RED up 64% since 2020 (5.3% → 8.7%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road condition sharply worse in 2024 — green fell from 77.4% to 72.5%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 58.1% rise in potholes filled with further increases projected
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits a progressively deteriorating network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 79% estate road network surveyed selectively, not comprehensively
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive maintenance share rising to a projected 28%
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN condition scorecard and documented inspection policy, your claim
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on an estate U-road, the selective SCANNER gap is your strongest structural argument</li>
              <li>• Whether the location falls under Torbay-only or shared Devon maintenance responsibility</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Torbay&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Torbay first
          </h2>
          <p className="text-surface-600 mb-6">
            Torbay&apos;s highway maintenance guidance states it cannot be everywhere every day and relies on
            public reports to supplement cyclic inspections. All reports are inspected within five working
            days. Reporting creates a dated record — useful evidence if the pothole was reported before your
            incident, or if SWISCo failed to repair it within a reasonable time.
          </p>
          <a
            href="https://www.torbay.gov.uk/roads/highway-fault/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Torbay Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            For emergencies call 01803 207720 (office hours) or 01803 701310 (24 hours). Keep your reference
            number and any confirmation.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Torbay?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-condition council still demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No estate-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No deteriorating-network admission cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 58.1% pothole rise and B/C decline documented</li>
                <li>✅ Selective U-road SCANNER gap argued</li>
                <li>✅ 14,974 four-year repair total cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Torbay</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=torbay"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Torbay claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The DfT rates Torbay GREEN for condition — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Condition scorecard is GREEN at network level, but Torbay&apos;s own SCANNER data
                shows B and C roads in RED condition rose from 5.3% in 2020 to 8.7% in 2024, and A-road RED
                rose from 2.6% to 3.9% in the same period. Section 58 turns on whether the specific defect
                that damaged your vehicle was reasonably inspected and repaired — not on an aggregate scorecard
                that can mask local deterioration.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an estate or residential road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 457.5km — 79% of Torbay&apos;s 576km network — and are predominantly
                estate roads. SCANNER surveys cover only the highest maintenance hierarchy U-roads, not the full
                estate network. SWISCo has commissioned a separate two-year RoadMetrics AI assessment of U-roads
                using PAS2161 methodology. If your road was outside the SCANNER route, network-level condition
                data may not exist for it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Torbay inspects every road twice a year — does that block Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. Torbay states each part of the highway will be inspected at least twice per
                year, and reports defects within five working days. But the council also admits a progressively
                deteriorating network and expects pothole attendances to keep rising. Twice-yearly driven
                inspections do not guarantee every defect is caught — especially on a network where reactive
                repairs climbed 58.1% in four years. Prior reports and photographic evidence of defect age
                remain decisive.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Torbay&apos;s 2024 risk-based inspection overhaul affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It may help or hinder depending on timing. Torbay overhauled its Highway Safety Inspection
                Manual in 2024, moving to a fully risk-based approach aligned with Well-Managed Highway
                Infrastructure guidance. If your incident occurred after the change, the council may argue its
                new system was reasonable. If the pothole was reported before the overhaul and not repaired,
                or if the defect predates the new criteria, the prior inspection regime and response times are
                still relevant.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole repairs rise 58.1% while condition is rated GREEN?
              </h3>
              <p className="text-surface-600 text-sm">
                Torbay filled 2,878 potholes in 2021/22 and 4,552 in 2024/25 — a 58.1% increase the council
                publishes itself. It simultaneously describes a progressively deteriorating network and projects
                at least 4,870 pothole repairs in 2025/26. Rising reactive workload on a network valued at over
                £700 million suggests defects are forming faster than structural renewal can keep pace, even
                where aggregate SCANNER scores remain acceptable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who maintains the dual carriageway between Paignton, Brixham and Torquay?
              </h3>
              <p className="text-surface-600 text-sm">
                Torbay&apos;s transparency report states maintenance responsibility for the high-speed dual
                carriageway between the South Devon Link Road and the eastern and western corridors linking
                Paignton and Brixham to Torquay is shared with Devon County Council. If your damage was on that
                section, establish which authority had maintenance duty at the precise location before
                submitting a claim.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I need to report the pothole before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Torbay&apos;s highway maintenance guidance states it inspects all reports within five working
                days and encourages residents to report defects it cannot catch on cyclic inspections alone.
                Reporting creates a dated council record — useful if the pothole was reported before your
                incident, or if SWISCo failed to repair it within a reasonable time. Keep your reference
                number and any confirmation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.torbay.gov.uk/council/policies/highways/lhmt-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Torbay Council Local Highways Maintenance Transparency Report 2025/2026
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
